import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

export interface VapidPublicKeyResponse {
  publicKey: string;
}

const VapidPublicKeyResponseSchema: z.ZodType<
  DataAndMessageResponse<VapidPublicKeyResponse>
> = z.object({
  data: z.object({
    publicKey: z.string(),
  }),
  message: z.string(),
});

const MessageResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const SendTestResponseSchema: z.ZodType<
  DataAndMessageResponse<{ sentCount: number }>
> = z.object({
  data: z.object({
    sentCount: z.number(),
  }),
  message: z.string(),
});

/**
 * DOM `PushSubscription` 과 구조적으로 호환되는 최소 타입.
 * 실제로 쓰는 것은 `toJSON()` 결과뿐이므로 DOM lib 이 없는 Node 소비자(share-log-mcp)도 이 모듈을 타입체크할 수 있게 한다.
 */
export interface PushSubscriptionLike {
  toJSON(): { endpoint?: string; keys?: Record<string, string> };
}

export function createPushModule(httpClient: HttpClient) {
  return {
    getVapidPublicKey: async (): Promise<string> => {
      const response = await httpClient.get("/api/v1/push/vapid-public-key", {
        schema: VapidPublicKeyResponseSchema,
      });
      return response.data.publicKey;
    },

    subscribe: async (
      subscription: PushSubscriptionLike,
    ): Promise<void> => {
      const subscriptionJSON = subscription.toJSON();
      await httpClient.post("/api/v1/push/subscribe", {
        data: {
          endpoint: subscriptionJSON.endpoint,
          keys: {
            p256dh: subscriptionJSON.keys?.p256dh,
            auth: subscriptionJSON.keys?.auth,
          },
        },
        schema: MessageResponseSchema,
      });
    },

    unsubscribe: async (endpoint: string): Promise<void> => {
      await httpClient.post("/api/v1/push/unsubscribe", {
        data: { endpoint },
        schema: MessageResponseSchema,
      });
    },

    sendTest: async (title?: string, body?: string): Promise<number> => {
      const response = await httpClient.post("/api/v1/push/test", {
        data: { title, body },
        schema: SendTestResponseSchema,
      });
      return response.data.sentCount;
    },
  };
}
